import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';
import { Activity, Mail, KeyRound, Lock, Loader2, ArrowLeft, CheckCircle2, Eye, EyeOff } from 'lucide-react';

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialEmail = searchParams.get('email') || '';

  const [step, setStep] = useState('email'); // email -> code -> newpass -> done
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const handleRequestCode = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/forgot-password`, { email: email.trim() });
      setEmailSent(res.data.email_sent);
      if (!res.data.email_sent && res.data.reset_token) {
        setResetToken(res.data.reset_token);
      }
      setStep('code');
      toast.success('Código enviado! Verifique seu email.');
    } catch {
      toast.error('Erro ao enviar código. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    if (code.length !== 6) { toast.error('Digite o código de 6 dígitos'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/verify-reset-code?code=${code}&email=${encodeURIComponent(email.trim())}`);
      setResetToken(res.data.reset_token);
      setStep('newpass');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Código inválido ou expirado');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error('A senha deve ter pelo menos 6 caracteres'); return; }
    if (password !== confirmPassword) { toast.error('As senhas não coincidem'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token: resetToken, password });
      setStep('done');
      toast.success('Senha redefinida com sucesso!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao redefinir senha');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <Toaster position="top-center" />
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Activity className="w-8 h-8 text-blue-600" />
            <span className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Vital</span>
          </div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Redefinir Senha
          </h1>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
          {/* Step: Email */}
          {step === 'email' && (
            <form onSubmit={handleRequestCode} className="space-y-5">
              <p className="text-sm text-slate-600 text-center mb-2">
                Digite seu email cadastrado para receber um código de redefinição.
              </p>
              <div className="space-y-2">
                <Label htmlFor="reset-email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="reset-email"
                    data-testid="reset-email-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="seu@email.com"
                    className="pl-10"
                    required
                  />
                </div>
              </div>
              <Button type="submit" data-testid="reset-send-code-btn" disabled={loading} className="w-full bg-slate-900 hover:bg-slate-800">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
                Enviar Código
              </Button>
              <button type="button" onClick={() => navigate('/')} className="w-full text-sm text-slate-500 hover:text-slate-700 flex items-center justify-center gap-1 mt-2">
                <ArrowLeft className="w-4 h-4" /> Voltar ao login
              </button>
            </form>
          )}

          {/* Step: Code */}
          {step === 'code' && (
            <form onSubmit={handleVerifyCode} className="space-y-5">
              <p className="text-sm text-slate-600 text-center mb-2">
                {emailSent
                  ? <>Enviamos um código de 6 dígitos para <strong>{email}</strong>.</>
                  : <>O envio de email falhou. Mas não se preocupe, <strong>use o código abaixo</strong> para continuar.</>
                }
              </p>
              {!emailSent && resetToken && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center">
                  <p className="text-xs text-amber-600 mb-1">Código temporário (email indisponível)</p>
                  <p className="text-sm text-amber-800">Use o token diretamente para redefinir:</p>
                  <Button type="button" variant="outline" className="mt-2 border-amber-300 text-amber-700" onClick={() => { setResetToken(resetToken); setStep('newpass'); }}>
                    Pular verificação de código
                  </Button>
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="reset-code">Código de 6 dígitos</Label>
                <div className="relative">
                  <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="reset-code"
                    data-testid="reset-code-input"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    className="pl-10 text-center text-2xl tracking-[0.5em] font-mono"
                    maxLength={6}
                    inputMode="numeric"
                    autoComplete="off"
                  />
                </div>
              </div>
              <Button type="submit" data-testid="reset-verify-code-btn" disabled={loading || code.length !== 6} className="w-full bg-slate-900 hover:bg-slate-800">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Verificar Código
              </Button>
              <button type="button" onClick={() => setStep('email')} className="w-full text-sm text-slate-500 hover:text-slate-700 flex items-center justify-center gap-1 mt-2">
                <ArrowLeft className="w-4 h-4" /> Trocar email
              </button>
            </form>
          )}

          {/* Step: New Password */}
          {step === 'newpass' && (
            <form onSubmit={handleResetPassword} className="space-y-5">
              <p className="text-sm text-slate-600 text-center mb-2">
                Defina sua nova senha abaixo.
              </p>
              <div className="space-y-2">
                <Label htmlFor="new-password">Nova Senha</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="new-password"
                    data-testid="reset-new-password-input"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Mínimo 6 caracteres"
                    className="pl-10 pr-10"
                    required
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirmar Senha</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="confirm-password"
                    data-testid="reset-confirm-password-input"
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Repita a nova senha"
                    className="pl-10"
                    required
                  />
                </div>
              </div>
              <Button type="submit" data-testid="reset-submit-btn" disabled={loading} className="w-full bg-slate-900 hover:bg-slate-800">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Lock className="w-4 h-4 mr-2" />}
                Redefinir Senha
              </Button>
            </form>
          )}

          {/* Step: Done */}
          {step === 'done' && (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Senha Redefinida!
              </h3>
              <p className="text-sm text-slate-600">
                Sua senha foi alterada com sucesso. Faça login com sua nova senha.
              </p>
              <Button onClick={() => navigate('/')} data-testid="reset-go-login-btn" className="w-full bg-slate-900 hover:bg-slate-800">
                Ir para Login
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
